import { PersonalizedSlipsMonetization } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { memo } from "react";
import { OutboundLabelForm } from "../outbound-labels";

export const DEFAULT_PERSONALIZED_SLIP_REVENUE_SHARE_PERCENT = 10;
export const DEFAULT_PERSONALIZED_SLIP_PER_INSERT_FEE_CENTS = 10;

export const personalizedSlipsMonetizationForm = groupInput({
  enabled: input<boolean>(),
  revenueSharePercent: input<string>({
    validator: numberValidator({ min: 0, max: 100 }),
  }),
  // Dollars in the UI; persisted as cents.
  perInsertFee: input<string>({ validator: numberValidator({ min: 0 }) }),
  refundRevenueShareOnReturn: input<boolean>(),
  perInsertFeeOnly: input<boolean>(),
});

export type PersonalizedSlipsMonetizationForm = InputSchema.Input<
  typeof personalizedSlipsMonetizationForm
>;
export type PersonalizedSlipsMonetizationValue = InputSchema.Value<
  typeof personalizedSlipsMonetizationForm
>;

export const personalizedSlipsMonetizationDefault: PersonalizedSlipsMonetizationValue =
  {
    enabled: false,
    revenueSharePercent: `${DEFAULT_PERSONALIZED_SLIP_REVENUE_SHARE_PERCENT}`,
    perInsertFee: (
      DEFAULT_PERSONALIZED_SLIP_PER_INSERT_FEE_CENTS / 100
    ).toFixed(2),
    refundRevenueShareOnReturn: false,
    perInsertFeeOnly: false,
  };

export function createPersonalizedSlipsMonetizationFormValue(
  loaded: PersonalizedSlipsMonetization | undefined,
): PersonalizedSlipsMonetizationValue {
  return {
    enabled: loaded?.enabled ?? false,
    revenueSharePercent: (
      loaded?.revenueSharePercent ??
      DEFAULT_PERSONALIZED_SLIP_REVENUE_SHARE_PERCENT
    ).toString(),
    perInsertFee: (
      (loaded?.perInsertFeeCents ??
        DEFAULT_PERSONALIZED_SLIP_PER_INSERT_FEE_CENTS) / 100
    ).toFixed(2),
    refundRevenueShareOnReturn: loaded?.refundRevenueShareOnReturn ?? false,
    perInsertFeeOnly: loaded?.perInsertFeeOnly ?? false,
  };
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

export function getUpdatedPersonalizedSlipsMonetization(
  value: PersonalizedSlipsMonetizationValue,
): PersonalizedSlipsMonetization {
  // Clamp/normalize here rather than trusting UI validation — PageForm disables
  // Save on invalid input but the submit path still persists whatever is set.
  const revenueSharePercent = isNaN(Number(value.revenueSharePercent))
    ? DEFAULT_PERSONALIZED_SLIP_REVENUE_SHARE_PERCENT
    : clamp(Number(value.revenueSharePercent), 0, 100);
  const perInsertFeeCents = isNaN(Number(value.perInsertFee))
    ? DEFAULT_PERSONALIZED_SLIP_PER_INSERT_FEE_CENTS
    : Math.max(0, Math.round(Number(value.perInsertFee) * 100));
  return {
    enabled: value.enabled,
    revenueSharePercent,
    perInsertFeeCents,
    refundRevenueShareOnReturn: value.refundRevenueShareOnReturn,
    perInsertFeeOnly: value.perInsertFeeOnly,
  };
}

export const PersonalizedSlipsSettingsCard = memo(
  function PersonalizedSlipsSettingsCard({
    input,
    canEditMonetization,
  }: {
    input: OutboundLabelForm;
    canEditMonetization: boolean;
  }) {
    const { personalizedSlipsEnabled, personalizedSlipsMonetization } =
      input.inputs;
    const {
      enabled,
      revenueSharePercent,
      perInsertFee,
      refundRevenueShareOnReturn,
      perInsertFeeOnly,
    } = personalizedSlipsMonetization.inputs;

    return (
      <Card title="Personalized inserts">
        <Flex dir="column" gap="lg">
          <RedoToggle
            label="Enable personalized inserts"
            setValue={personalizedSlipsEnabled.setValue}
            value={personalizedSlipsEnabled.value}
          />
          <Text fontSize="xs" textColor="secondary">
            When enabled, merchants can create and print personalized inserts
            with AI-generated content, product recommendations, and discount
            codes.
          </Text>

          <RedoToggle
            disabled={!canEditMonetization}
            label="Enable monetization"
            setValue={enabled.setValue}
            value={enabled.value}
          />
          <Text fontSize="xs" textColor="secondary">
            Merchants cannot access inserts until monetization is enabled here
            and the merchant accepts the payment terms in-product.
          </Text>

          {enabled.value && (
            <>
              <Flex dir="column">
                <RedoTextInput
                  IconTrailing={() => (
                    <Text fontSize="sm" textColor="secondary">
                      USD
                    </Text>
                  )}
                  label={
                    perInsertFeeOnly.value
                      ? "Per-insert fee (every insert)"
                      : "Per-insert fee (no discount code)"
                  }
                  min={0}
                  placeholder="0.10"
                  setValue={perInsertFee.setValue}
                  size="md"
                  state={!canEditMonetization ? "disabled" : undefined}
                  step={0.01}
                  value={perInsertFee.value}
                />
                {perInsertFee.errors.length > 0 && (
                  <Text fontSize="xs" textColor="error">
                    {perInsertFee.errors[0]}
                  </Text>
                )}
              </Flex>

              <RedoToggle
                disabled={!canEditMonetization}
                label="Per-insert fee only (never charge revenue share)"
                setValue={perInsertFeeOnly.setValue}
                value={perInsertFeeOnly.value}
              />
              <Text fontSize="xs" textColor="secondary">
                When enabled, every printed insert is charged the per-insert fee
                and revenue share is never charged, even for inserts that carry
                a discount code.
              </Text>

              {!perInsertFeeOnly.value && (
                <>
                  <Flex dir="column">
                    <RedoTextInput
                      IconTrailing={() => (
                        <Text fontSize="sm" textColor="secondary">
                          %
                        </Text>
                      )}
                      label="Revenue share (discount-code purchases)"
                      max={100}
                      min={0}
                      placeholder="10"
                      setValue={revenueSharePercent.setValue}
                      size="md"
                      state={!canEditMonetization ? "disabled" : undefined}
                      step={0.1}
                      value={revenueSharePercent.value}
                    />
                    {revenueSharePercent.errors.length > 0 && (
                      <Text fontSize="xs" textColor="error">
                        {revenueSharePercent.errors[0]}
                      </Text>
                    )}
                  </Flex>
                  <RedoToggle
                    disabled={!canEditMonetization}
                    label="Refund revenue share on returns"
                    setValue={refundRevenueShareOnReturn.setValue}
                    value={refundRevenueShareOnReturn.value}
                  />
                  <Text fontSize="xs" textColor="secondary">
                    When enabled, a processed return refunds the revenue share
                    for items refunded as money back. Exchanges and store credit
                    are excluded (the merchant kept the revenue).
                  </Text>
                </>
              )}
            </>
          )}
        </Flex>
      </Card>
    );
  },
);
